import React, { useEffect, useState } from 'react';
import {
    Button,
    Table, TableHead, TableRow, TableCell, TableBody
} from '@material-ui/core';

import fileDownload from 'js-file-download';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';

import { get, post, download } from "../fetchers/fetchers";
import { IState, IControlMeasure, IControlMeasureAssessment, IControlMeasureCost, IImplementation, IPathway, ImplemenationPathway, IAssessmentSettings } from "../types/types";
import { useHistory } from "react-router-dom";
import { API_URL } from "../env";
import { FormattedInput } from '@h-a-r-c/react-formatted-input';
import { is } from 'date-fns/locale';

interface ImplementationProps {
    assessmentId: number;
    parentState: IState | undefined,
    implementation_id: number;
    callbackReload: () => void;
}

interface ImplementationState {
    controlMeasures: IControlMeasure[];
    state: IState;
    prerequisiteCMS: number[];
}

interface ControlMeasureCostState {
    controlMeasureCosts: IControlMeasureCost[];
    state: IState;

}

interface IPathwayImplemenation extends ImplemenationPathway {
    individual_risk_after?: number;
    societal_risk_after?: number;
    economic_risk_after?: number;
}

const Implementation: React.FC<ImplementationProps> = ({
    assessmentId,
    parentState,
    implementation_id,
    callbackReload
}) => {

    let history = useHistory();
    const [controlMeasures, setControlMeasures] = useState<IControlMeasure[]>([]);
    const [implementation, setImplementation] = useState<IImplementation | undefined>(undefined);
    const [controlMeasureCosts, setControlMeasureCosts] = useState<IControlMeasureCost[]>([]);
    const [controlMeasureAssessments, setControlMeasureAssessments] = useState<IControlMeasureAssessment[]>([]);
    const [currentAssessmentSettings, setCurrentAssessmentSettings] = useState<IAssessmentSettings>();
    const [implemented, setImplemented] = useState<ImplementationState[]>([]);
    const [loaded, setLoaded] = useState<boolean>(false);
    const [lastIndex, setLastIndex] = useState<number>(0);
    const [sourceControlMeasures, setSourceControlMeasures] = useState<IControlMeasure[]>([]);
    const [destinationControlMeasures, setDestinationControlMeasures] = useState<IControlMeasure[]>([]);
    const [stateControlMeasureCosts, setStateControlMeasureCosts] = useState<ControlMeasureCostState[]>([]);
    const [currentControlMeasure, setCurrentControlMeasure] = useState<IControlMeasure | undefined>(undefined);
    const [currentControlMeasureCost, setCurrentControlMeasureCost] = useState<IControlMeasureCost | undefined>(undefined);
    const [currentPathway, setCurrentPathway] = useState<IPathway | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);

    const updateControlMeasureCost = (controlMeasureCost: IControlMeasureCost, path: IPathway) => {

        let c = { ...controlMeasureCost }

        delete c.id
        delete c.cssl
        delete c.df
        delete c.annualised_cost
        delete c.cost_adequacy
        delete c.cost_adequacy_id
        delete c.document


        setControlMeasureCost(c, path).then(
            () => {
                // callbackReload();
                setLoaded(false);
            })
    }


    const loadImplementations = async () => {
        const d = await get<IImplementation[]>(API_URL + "implementation/" + assessmentId + "/");
    
        // fiter implementations to state_id
        let filtered_d = d.filter(i => i.state_uuid === parentState?.uuid);
    
    
        return filtered_d;
      }

    const loadAssessmentSettings = async () => {
        const d = await get<IAssessmentSettings>(API_URL + "assessment_project/" + assessmentId + "/settings");
        setCurrentAssessmentSettings(d);
        return d;
    }
    


    const updateControlMeasuresCost = (cm: IControlMeasureCost) => {
        if (!currentPathway) {
            return;
        }

        const updatedCost = { ...cm };
        updateControlMeasureCost(updatedCost, currentPathway);
        setCurrentControlMeasureCost(updatedCost);
    };

    useEffect(() => {
        if (!parentState || loaded) {
            return;
        }
        console.log("Loading implementation data...");
        let isCancelled = false;

        const fetchData = async () => {
            setLoading(true);
            setDestinationControlMeasures([]);
            setSourceControlMeasures([]);
            setCurrentControlMeasure(undefined);
            setCurrentControlMeasureCost(undefined);
            setCurrentPathway(undefined);
            setControlMeasureAssessments([]);
            setCurrentAssessmentSettings(undefined);
            setStateControlMeasureCosts([]);

            try {
                const baseCostsPromise = getControlMeasureCosts();
                const [
                    implementations,
                    assessmentSettings,
                    controlMeasuresResponse,
                    controlMeasureAssessmentsResponse,
                    statesResponse
                ] = await Promise.all([
                    loadImplementations(),
                    loadAssessmentSettings(),
                    loadControlMeasures(),
                    getControlMeasureAssessment(),
                    fetchStates()
                ]);


               

                await baseCostsPromise;

                console.log("statesResponse", statesResponse, isCancelled);

                if (isCancelled) {
                    return;
                }

                const selectedImplementation = implementations.find(i => i.id === implementation_id);

                if (!selectedImplementation) {
                    setImplementation(undefined);
                    setImplemented([]);
                    setLastIndex(0);
                    return;
                }

                setImplementation(selectedImplementation);

                setControlMeasureAssessments(controlMeasureAssessmentsResponse);

                console.log("statesResponse 1", statesResponse);

                const relevantStates = statesResponse.filter(
                    (state) =>
                        state.uuid === parentState.uuid ||
                        selectedImplementation.pathways?.some(p => p.implemented_state_uuid === state.uuid)
                );

                const [implementedStates, stateCosts] = await Promise.all([
                    getImplementedControlIDs(controlMeasuresResponse, relevantStates, selectedImplementation.pathways),
                    Promise.all(
                        relevantStates.map(async (state) => ({
                            state,
                            controlMeasureCosts: await getStateControlMeasureCosts(state)
                        }))
                    )
                ]);
                console.log("stateCosts", stateCosts);
                if (isCancelled) {
                    return;
                }

                console.log("pathways", selectedImplementation.pathways);

                preorderControlMeasures(controlMeasuresResponse, implementedStates);
                setStateControlMeasureCosts(stateCosts);
            } catch (error) {
                console.error("Failed to load implementation data", error);
            } finally {
                setLoading(false);
                if (!isCancelled) {
                    setLoaded(true);
                }
            }
        };

        fetchData();

        return () => {
            isCancelled = true;
        };
    }, [parentState, implementation_id, assessmentId, loaded]);


    useEffect(() => {
        console.log("implemenation", controlMeasures);
        if (!implementation) {
            return;
        }
        if (controlMeasures.length === 0) {
            return;
        }
        console.log("measures", controlMeasures, controlMeasureAssessments);
        if (controlMeasureAssessments.length === 0) {
            return;
        }

        filterControlMeasures(
            controlMeasures,
            controlMeasureAssessments,
            implementation,
            currentAssessmentSettings
        );
    }, [controlMeasures, controlMeasureAssessments, implementation, currentAssessmentSettings]);

    const filterControlMeasures = (
        control_measures: IControlMeasure[],
        cms_a: IControlMeasureAssessment[],
        imp: IImplementation,
        assessmentSettings?: IAssessmentSettings
    ) => {
        console.log("Filtering control measures with settings:", cms_a);
        const isControlCentric = assessmentSettings?.is_failure_mode_centric || false;

        let filtered = control_measures.filter(c =>
            getMinAssessmentIndex(c, cms_a, isControlCentric) < 3 &&
            getMinAssessmentIndex(c, cms_a, isControlCentric) > 0 &&
            isConsidered(c, cms_a)
        );

        let current = imp;
        let pw = current.pathways || [];
        let ordered = pw.sort((a, b) => {
            if (a.order > b.order) {
                return 1;
            }
            if (a.order < b.order) {
                return -1;
            }
            return 0;
        }
        );
        let cms = ordered.map(p => p.control_measure_uuid);

        let cms_ordered = filtered.filter(c => cms.includes(c.uuid));

        // order cms_order using ordered
        cms_ordered.sort((a, b) => {
            let a_index = cms.indexOf(a.uuid);
            let b_index = cms.indexOf(b.uuid);

            if (a_index > b_index) {
                return 1;
            }
            if (a_index < b_index) {
                return -1;
            }
            return 0;
        }
        );

        // order 
        filtered = filtered.filter(c => !cms.includes(c.uuid));

        setDestinationControlMeasures( cms_ordered);

        console.log("Filtered control measures:", filtered, control_measures, cms_a);

        setSourceControlMeasures(filtered);
        return filtered;
    };

    const isCurrentImplemenation = (controlMeasure: IControlMeasure, index: number): boolean => {
        const implemented_cms = getImplementedCMS(controlMeasure);
        const successor = getPathway((implemented_cms?.order || -1) + 1);

        if (implemented_cms) {
            if (implemented_cms.implemented_state_uuid === null && implemented_cms.order === 1) {
                return true;
            }
            if (successor) {
                if (implemented_cms.implemented_state_uuid !== null && successor.implemented_state_uuid === null) {
                    return true;
                }
                if (implemented_cms.implemented_state_uuid !== null && successor.implemented_state_uuid !== null) {
                    return false;
                }

            }
            if (implemented_cms.implemented_state_uuid !== null) {
                return true;
            }
        }
        return false;
    }


    useEffect(() => {
        if (!loaded) return;
        // find which destination control measures is currently being edited and index
        let current_cms = destinationControlMeasures.filter((c, index) => isCurrentImplemenation(c, index));
        let order = destinationControlMeasures.findIndex((c, index) => isCurrentImplemenation(c, index));


        if (current_cms.length === 0) {
            setCurrentControlMeasure(undefined);
            return;
        }

        const cms = current_cms[0];

        setCurrentControlMeasure(cms);
        const pathway = getImplementedCMS(cms)
        setCurrentPathway(pathway);

        const cmscost = getControlStateMeasureCost(cms, order);
        setCurrentControlMeasureCost(cmscost);


    }, [destinationControlMeasures, loaded, stateControlMeasureCosts, implemented, setStateControlMeasureCosts])


    const preorderControlMeasures = (cms_g: IControlMeasure[], implemented: ImplementationState[]) => {
        let imp = cms_g.filter(c => implemented.some(i => i.controlMeasures.some(cm => cm.uuid === c.uuid)));

        let ordered = imp.sort((a, b) => {
            // find the prerequisite list for each control measure
            let prereqA = implemented.find(i => i.controlMeasures.some(c => c.uuid === a.uuid))?.prerequisiteCMS || [];
            let prereqB = implemented.find(i => i.controlMeasures.some(c => c.uuid === b.uuid))?.prerequisiteCMS || [];

            if (prereqA.length > prereqB.length) {
                return 1;
            }
            if (prereqA.length < prereqB.length) {
                return -1;
            }
            return 0;
        }
        )

        imp = cms_g.filter(c => !implemented.some(i => i.controlMeasures.some(cm => cm.uuid === c.uuid)));

        ordered = ordered.concat(imp);
        console.log("Preordered control measures:", ordered);

        setControlMeasures(ordered);

    }

    const postImplemenationPathway = async (pathway: ImplemenationPathway) => {

        const d = await post<ImplemenationPathway>(API_URL + "implementation/" + assessmentId + "/pathway/implementation", pathway);
        // callbackReload();
        setLoaded(false);
    }


    const postImplementPathway = async (pathway: IPathwayImplemenation) => {
        const d = await post<IPathwayImplemenation>(API_URL + "implementation/" + assessmentId + "/pathway/implementation/create", pathway);
        setLoaded(false);
    }

    const createImplementation = (path: IPathway) => {

        // expand IPathway type with individual_risk_after
        let p: IPathwayImplemenation = {
            assessment_project_id: path.assessment_project_id,
            control_measure_uuid: path.control_measure_uuid,
            state_uuid: path.state_uuid,
            order: path.order,
            individual_risk_after: 10,
            societal_risk_after: 10,
            economic_risk_after: 10,
            implemenation_id: implementation?.id || 0,
        }

        postImplementPathway(p)
        

    }

    const postPathway = async (pathway: IPathway) => {
        // check if pathway.implementation_id is null otherwise remove
        let p = { ...pathway }
        if (pathway.implemented_state_uuid === null) {
            delete p.implemented_state_uuid
        }

        await post<IPathway>(API_URL + "implementation/" + assessmentId + "/pathway", p);
        const ip: ImplemenationPathway = {
            implemenation_id: implementation?.id || 0,
            state_uuid: implementation?.state_uuid || 0,
            assessment_project_id: pathway.assessment_project_id,
            order: pathway.order,
            control_measure_uuid: pathway.control_measure_uuid,
        }
        postImplemenationPathway(ip);

    }

    const loadControlMeasures = async () => {
        const d = await get<IControlMeasure[]>(API_URL + "control_measures/" + assessmentId + "/?state=" + parentState?.uuid);
        let filtered = d.filter(c => !c.existing).filter(c => c.is_active);

        setControlMeasures(filtered);

        return filtered
    }


    const getControlMeasureAssessment = async () => {
        return await get<IControlMeasureAssessment[]>(API_URL + "control_measures/" + assessmentId + "/assessments" + "?state=" + parentState?.uuid);
    }

    const getStateControlMeasureCosts = async (state: IState) => {
        const d = await get<IControlMeasureCost[]>(API_URL + "control_measures/" + assessmentId + "/costs" + "?state=" + state.uuid);
        return d
    }

    const getControlMeasureCosts = async () => {
        const d = await get<IControlMeasureCost[]>(API_URL + "control_measures/" + assessmentId + "/costs" + "?state=" + parentState?.uuid);
        setControlMeasureCosts(d)
    }

    const fetchStates = async () => {
        return await get<IState[]>(API_URL + "assessment_project/" + assessmentId + "/states");
    };

    const getImplementedControlIDs = async (cms: IControlMeasure[], s: IState[], pathway?: IPathway[]) => {
        // go over all children and get the implemented control measures ids
        let implementedControls: ImplementationState[] = [];

        
        if (!pathway) {
            setImplemented(implementedControls);
            setLastIndex(implementedControls.length);

            return implementedControls;
        }

        let prereq: number[] = [];

        pathway.forEach((p) => {
            const state = s.find((stateItem) => stateItem.uuid === p.implemented_state_uuid);
            if (!state) {
                return;
            }

            const matchingControlMeasures = cms.filter((control) => control.uuid === p.control_measure_uuid);
            if (matchingControlMeasures.length === 0) {
                return;
            }

            implementedControls.push({ controlMeasures: matchingControlMeasures, state, prerequisiteCMS: prereq });
            prereq = [...prereq, matchingControlMeasures[0].uuid];
        });


        setImplemented(implementedControls);
        setLastIndex(implementedControls.length);
        return implementedControls;
    };

    const getImplementedControls = async (cms_g: IControlMeasure[], state: IState) => {
        const cms = await get<IControlMeasure[]>(API_URL + "control_measures/" + assessmentId + "/?state=" + state?.uuid);

        let prereq = cms.filter(c => c.existing).map(c => c.uuid);
        // only keep prereq that are part of the control measures
        prereq = prereq.filter(p => cms_g.some(c => c.uuid === p));

        let implementation = {
            controlMeasures: cms.filter(c => !c.existing),
            state: state,
            prerequisiteCMS: prereq

        }
        return implementation;
    };

    const overall_asessment = ["Reasonably Practicable",
        "Reasonably Practicable (tentative)",
        "Not Reasonably Practicable (tentative)",
        "Not Reasonably Practicable",
        "Cannot Determine at this Time"
    ]

    const updatePathways = (cms: IControlMeasure[]) => {

        let pathways = implementation?.pathways || [];

        cms.map((c, index) => {
            let p = {
                assessment_project_id: assessmentId,
                control_measure_uuid: c.uuid,
                state_uuid: parentState?.uuid || 0,
                order: index + 1
            }

            // check if pathway exists and order is the same
            let existing = pathways.find(p => p.control_measure_uuid === c.uuid && p.order === index + 1);
            if (existing) {
                return;
            }
            postPathway(p);

        })


    }

    const handleDragEnd = (result: DropResult) => {
        const { source, destination } = result;

        if (!destination) return;

        if (source.droppableId !== destination.droppableId) {
            const sourceItems = Array.from(sourceControlMeasures);
            const destItems = Array.from(destinationControlMeasures);
            const [movedItem] = sourceItems.splice(source.index, 1);
            destItems.splice(destination.index, 0, movedItem);
            updatePathways(destItems);
            setSourceControlMeasures(sourceItems);
            setDestinationControlMeasures(destItems);
        } else {

            const items = Array.from(destinationControlMeasures);
            const [reorderedItem] = items.splice(result.source.index, 1);
            items.splice(destination.index, 0, reorderedItem);
            updatePathways(items)
            setDestinationControlMeasures(items);


        }
    };

    const getMinAssessmentIndex = (controlMeasure: IControlMeasure, cma_s: IControlMeasureAssessment[], controlMeasureCentric: boolean): number => {
        let perfilter_assessment = cma_s;

        if (controlMeasureCentric) {
            perfilter_assessment = perfilter_assessment.filter(c => c.project_failure_mode_id === -1);
        } else {
            perfilter_assessment = perfilter_assessment.filter(c => c.project_failure_mode_id > 0);
        }

        let assessments = perfilter_assessment.filter(c => c.control_measure_id === controlMeasure.uuid);

        console.log("perfilter_assessment",controlMeasure.description, controlMeasure.uuid, cma_s, perfilter_assessment, controlMeasureCentric, assessments);

        let min = 0;
        assessments.forEach(a => {
            let index = a.overall_assessment_id || 0;

            if (index === 0) {
                return;
            }
            if (min === 0) {
                min = index;
                return;
            }
            if (index < min) {
                min = index;
            }
        });

        console.log("Min assessment for", controlMeasure.description, min);

        return min;
    }

    const isConsidered = (controlMeasure: IControlMeasure, cma_s: IControlMeasureAssessment[]): boolean => {
        let assessments = cma_s.filter(c => c.control_measure_id === controlMeasure.uuid)
        for (let a of assessments) {
            if (a.considered) {
                return true;
            }
        }
        return false;
    }

    const getMinAssessment = (controlMeasure: IControlMeasure) => {
        let min = getMinAssessmentIndex(controlMeasure, controlMeasureAssessments, currentAssessmentSettings?.is_failure_mode_centric || false);

        if (min === 0) {
            return "-"
        }
        return overall_asessment[min - 1];
    }

    if (!parentState) {
        return <div></div>
    }

    const checkIfImplemented = (controlMeasure: IControlMeasure) => {

        let implementedControl = implemented.filter(i => i.controlMeasures.some(c => c.uuid === controlMeasure.uuid));
        if (implementedControl.length > 0) {
            return true;
        }
        return false;

    }

    const handleProjectClick = (id: number, state_uuid?: number, access?: boolean) => {
        if (state_uuid) {
            if (access) {
                history.push("/assessment/" + id + "?state=" + state_uuid + "&access=true")
            } else {
                history.push("/assessment/" + id + "?state=" + state_uuid)
            }
        } else {
            history.push("/assessment/" + id)
        }
    }


    const downloadFile = async (id: number) => {
        return await download(API_URL + "risk_assessments/" + id + "/download_csv");
    }

    const handleDownload = (id: number, state_uuid?: number) => {
        // open the project in a new tab
        downloadFile(id).then(({ data, filename }) => { fileDownload(data, "assessment_" + id + ".csv") })

    }

    const getControlStateMeasureCost = (controlMeasure: IControlMeasure, index: number): IControlMeasureCost => {
        if (checkIfImplemented(controlMeasure)) {
            let cms = implemented.filter(i => i.controlMeasures.some(c => c.uuid === controlMeasure.uuid));
            let cm = cms[0]
            let state = cm.state;
            let costs = stateControlMeasureCosts.find(s => s.state.uuid === state.uuid)?.controlMeasureCosts.filter(c => c.control_measure_id === controlMeasure.uuid);

            if (costs && costs.length > 0) {
                return costs[0];
            }
        }
        
        const cmc = getCMeasureCost(controlMeasure)
        return cmc
    }

    const getImplementedCMS = (controlMeasure: IControlMeasure): IPathway | undefined => {
        if (implementation && implementation.pathways) {
            let implemented_cms = implementation.pathways.filter(c => c.control_measure_uuid === controlMeasure.uuid);
            if (implemented_cms.length === 1) {
                return implemented_cms[0];
            }
        }
    }

    const getPathway = (order: number): IPathway | undefined => {
        if (implementation && implementation.pathways) {
            let implemented_cms = implementation.pathways.filter(c => c.order === order);
            if (implemented_cms.length === 1) {
                return implemented_cms[0];
            }
        }
    }

    const predecessorState = (controlMeasure: IControlMeasure, index: number): IPathway | undefined => {
        if (implementation && implementation.pathways) {
            const implemented_cms = getImplementedCMS(controlMeasure);
            if (implemented_cms) {
                // get order - 1 pathway
                let order = implemented_cms.order - 1;
                return getPathway(order);
            }
        }
    }



    const renderUploadButton = (controlMeasure: IControlMeasure, index: number) => {
        const implemented_cms = getImplementedCMS(controlMeasure);
        const predecessor = predecessorState(controlMeasure, index);
        const successor = getPathway((implemented_cms?.order || -1) + 1);

        if (implemented_cms) {

            if (implemented_cms.implemented_state_uuid === null && implemented_cms.order === 1) {

                return <>
                    <Button variant="outlined" size="small" onClick={prevState => createImplementation(implemented_cms)} >Create</Button>
                    <Button variant="outlined" size="small" onClick={prevState => handleProjectClick(assessmentId ?? 0, implemented_cms.implemented_state_uuid, true)}>Edit</Button>
                </>
            }

            if (successor) {
                if (implemented_cms.implemented_state_uuid !== null && successor.implemented_state_uuid === null) {
                    return (<div>
                        <Button variant="outlined" size="small" onClick={prevState => handleProjectClick(assessmentId ?? 0, implemented_cms.implemented_state_uuid, true)}>Edit</Button>
                        <><Button variant="outlined" size="small" onClick={prevState => createImplementation(successor)} >Close</Button></></div>)
                }
                if (implemented_cms.implemented_state_uuid !== null && successor.implemented_state_uuid !== null) {
                    return <><Button variant="outlined" size="small"  > View</Button></>
                }
            }
            if (implemented_cms.implemented_state_uuid !== null) {
                return <Button variant="outlined" size="small" onClick={prevState => handleProjectClick(assessmentId ?? 0, implemented_cms.implemented_state_uuid, true)}>Edit</Button>
            }
        }
        return <div>-</div>
    }


    const renderDownloadButton = (controlMeasure: IControlMeasure, index: number) => {
        return <div><Button variant="outlined" size="small" onClick={prevState => handleDownload(assessmentId)}>Download</Button></div>
    }


    const getCMeasureCost = (cm: IControlMeasure): IControlMeasureCost => {
        let cm_id = cm.uuid
        if (!cm_id)
            cm_id = -1
        let controlMeasureCost = controlMeasureCosts.find(cma => cma.control_measure_id === cm_id)

        if (controlMeasureCost) {
            return controlMeasureCost
        }
        controlMeasureCost = {
            control_measure_id: cm_id
        }
        return controlMeasureCost
    }

    const setControlMeasureCost = async (controlMeasureCost: IControlMeasureCost, path: IPathway) => {
        let c = { ...controlMeasureCost }

        delete c.id
        delete c.created_at

        await post<IControlMeasureCost>(API_URL + "control_measures/" + assessmentId + "/costs", c, path.implemented_state_uuid);


    }

    return (
        <>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="controlMeasuresSource">
                    {(provided) => (
                        <Table {...provided.droppableProps} ref={provided.innerRef}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Order</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>SFAIRP Assesssment</TableCell>
                                    <TableCell>Societal Risk Before</TableCell>
                                    <TableCell>Societal Risk After</TableCell>
                                    <TableCell>Template</TableCell>
                                    <TableCell>State</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {destinationControlMeasures.map((controlMeasure, index) => {
                                    const isCurrent = currentControlMeasure?.uuid === controlMeasure.uuid;
                                    const resolvedCost = isCurrent && currentControlMeasureCost
                                        ? currentControlMeasureCost
                                        : getControlStateMeasureCost(controlMeasure, index);

                                    return (
                                        <Draggable key={controlMeasure.uuid} draggableId={controlMeasure.uuid.toString()} index={index}>
                                            {(provided) => (
                                                <TableRow {...provided.draggableProps} {...provided.dragHandleProps} ref={provided.innerRef}>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>{controlMeasure.description}</TableCell>
                                                    <TableCell>{getMinAssessment(controlMeasure)}</TableCell>
                                                    <TableCell>
                                                        <FormattedInput
                                                            obj={resolvedCost}
                                                            fieldKey={"societal_risk_before"}
                                                            format={"scientific"}
                                                            setObj={setCurrentControlMeasureCost}
                                                            updateObj={updateControlMeasuresCost}
                                                            write_access={isCurrent}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <FormattedInput
                                                            obj={resolvedCost}
                                                            fieldKey={"societal_risk_after"}
                                                            format={"scientific"}
                                                            setObj={setCurrentControlMeasureCost}
                                                            updateObj={updateControlMeasuresCost}
                                                            write_access={isCurrent}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{renderDownloadButton(controlMeasure, index)}</TableCell>
                                                    <TableCell>{renderUploadButton(controlMeasure, index)}</TableCell>
                                                </TableRow>
                                            )}
                                        </Draggable>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </Droppable>

                <h2>Reasonable Practicable and Reasonable Practicable (tentative) Control Measures</h2>

                <Droppable droppableId="controlMeasuresDestination">
                    {(provided) => (
                        <Table {...provided.droppableProps} ref={provided.innerRef}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>SFAIRP Assesssment</TableCell>
                                    <TableCell>Societal Risk Before</TableCell>
                                    <TableCell>Societal Risk After</TableCell>

                                    {/* <TableCell>State</TableCell> */}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sourceControlMeasures.map((controlMeasure, index) => {
                                    const cost = getControlStateMeasureCost(controlMeasure, index);

                                    return (
                                        <Draggable key={controlMeasure.uuid} draggableId={controlMeasure.uuid.toString()} index={index}>
                                            {(provided) => (
                                                <TableRow {...provided.draggableProps} {...provided.dragHandleProps} ref={provided.innerRef}>
                                                    {/* <TableCell>{index + 1}</TableCell> */}
                                                    <TableCell>{controlMeasure.description}</TableCell>
                                                    <TableCell>{getMinAssessment(controlMeasure)}</TableCell>
                                                    <TableCell>{cost.societal_risk_before?.toExponential(2)}</TableCell>
                                                    <TableCell>{cost.societal_risk_after?.toExponential(2)}</TableCell>

                                                    {/* <TableCell>{renderUploadButton(controlMeasure, index)}</TableCell> */}
                                                </TableRow>
                                            )}
                                        </Draggable>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </Droppable>
            </DragDropContext>
        </>
    );
};

export default Implementation;
